import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Skeleton } from "antd";
import type { HookAPI } from "antd/es/modal/useModal/index";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { actions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import ModalFooter from "../../common/components/ModalFooter";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { routeNames } from "../common/routes.ts";
import { ticketDataActions } from "../../features/ticket-data/ticketDataSlice";
import { ticketActions } from "../../features/ticket/ticketSlice";
import type {
	Blade,
	Client,
	Location,
	Materials,
	Project,
	Services,
	Ticket,
	Turbine,
	UserLimitedList,
} from "../generated-axios-client/index.ts";
import { TicketFields } from "./TicketFields";

type CreateTicketFormProps = {
	dispatch: Dispatch;
	initialValue?: Partial<Ticket> | null;
	intl: IntlShape;
	serviceItems: Services[] | null;
	userItems?: UserLimitedList[];
	coordinatorItems?: UserLimitedList[];
	turbineItems?: Turbine[];
	clientItems?: Client[];
	bladeItems?: Blade[];
	locationItems?: Location[] | null;
	materialItems?: Materials[] | null;
	projectItems?: Project[];
	modalAPI?: HookAPI;
};

const fields = new Set(["turbine", "client"]);

export function CreateTicketForm(props: CreateTicketFormProps) {
	const {
		dispatch,
		initialValue,
		intl,
		serviceItems,
		userItems,
		coordinatorItems,
		turbineItems,
		clientItems,
		bladeItems,
		locationItems,
		materialItems,
		projectItems,
		modalAPI,
	} = props;

	const [form] = Form.useForm();

	const saveTicket = () => {
		form
			.validateFields()
			.then((values) => {
				const cleanedItems = (
					(values.items ?? []) as Array<Record<string, unknown>>
				).map((item) => ({
					...item,
					service: (item.service as string | undefined) ?? null,
					description: (item.description as string | undefined) ?? null,
					estimated_time_spent:
						item.estimated_time_spent != null
							? String(item.estimated_time_spent as number)
							: null,
					completed: (item.completed as boolean | undefined) ?? false,
					new_files: (item.new_files as Record<string, unknown> | undefined) ?? {},
				}));

				const editedValues = {
					...values,
					items: cleanedItems,
					new_files: values.new_files ?? {},
					coordinators:
						(values.coordinators as number[] | undefined)?.map((id) => ({
							coordinator: id,
						})) ?? [],
					assignees:
						(values.assignees as number[] | undefined)?.map((id) => ({
							assignee: id,
						})) ?? [],
					start: values.start ? values.start.format("YYYY-MM-DD") : null,
					deadline: values.deadline
						? values.deadline.format("YYYY-MM-DD")
						: null,
					client_case_nr: values.client_case_nr,
					materials: (values.materials as string[] | undefined)?.map((id) => ({
						material: id,
					})),
				};

				if (initialValue?.id) {
					editedValues.id = initialValue.id;
				}

				dispatch(ticketActions.addOrUpdateTicket(editedValues));
			})
			.catch(() => {});
	};

	const initialValues = useMemo(() => {
		return initialValue ? { ...initialValue } : undefined;
	}, [initialValue]);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	const relevantFieldsChanged = (
		changedValues: Record<string, unknown>,
		allValues: Record<string, unknown>,
	) => {
		if ("service" in changedValues) {
			const newServiceId = changedValues.service as string | undefined;
			if (newServiceId) {
				const service = serviceItems?.find((s) => s.id === newServiceId);
				const materialIds = (service?.materials ?? [])
					.map((m) => m.material)
					.filter((id): id is string => !!id);
				form.setFieldValue("materials", materialIds);
			} else {
				form.setFieldValue("materials", []);
			}
		}

		// The site is derived from the turbine, so it must not be sent as a filter:
		// it would narrow the turbine list to the previously derived site and the
		// turbine selector would drop the turbine that was just picked.
		for (const [key, value] of Object.entries(changedValues)) {
			if (
				(fields.has(key) && value) ||
				(fields.has(key) && key !== "turbine")
			) {
				dispatch(
					ticketDataActions.fetchTicketData({
						client: allValues.client as string | undefined,
						turbine: allValues.turbine as string | undefined,
					}),
				);
			}
		}
	};

	if (!serviceItems || !userItems || !clientItems || !locationItems) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "ticket_create_title" })}
				subtitle={intl.formatMessage({ id: "ticket_create_subtitle" })}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={initialValues}
					onValuesChange={(changedValues, allValues) =>
						relevantFieldsChanged(changedValues, allValues)
					}
				>
					<TicketFields
						intl={intl}
						serviceItems={serviceItems}
						userItems={userItems}
						coordinatorItems={coordinatorItems ?? []}
						turbineItems={turbineItems}
						clientItems={clientItems}
						bladeItems={bladeItems}
						locationItems={locationItems}
						materialItems={materialItems ?? []}
						projectItems={projectItems ?? []}
						form={form}
						dispatch={dispatch}
						modalAPI={modalAPI}
					/>
				</Form>
				<ModalFooter>
					<Button
						onClick={() => dispatch(actions.navigateTo(routeNames.TICKET_LIST))}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" onClick={saveTicket}>
						{intl.formatMessage({ id: "ticket_button_title_create" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const CreateTicketFormConnected = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	serviceItems: state.ticket.services,
	userItems: state.ticketData.technicians,
	coordinatorItems: state.ticketData.coordinators,
	turbineItems: state.ticketData.turbines,
	initialValue: state.ticket.initialValue,
	clientItems: state.ticketData.clients,
	bladeItems: state.ticketData.blades,
	locationItems: state.ticketData.locations,
	materialItems: state.ticketData.materials,
	projectItems: state.ticketData.projects ?? [],
	modalAPI: state.appFeedback.modal,
}))(CreateTicketForm);

export default CreateTicketFormConnected;
